import { Fragment, useRef, useState } from "react"

import { App, Badge, Divider, Dropdown, Flex, Typography } from "antd"
import cls from "classname"
import { useDispatch, useSelector } from "react-redux"

import Icon from "../../../../Icon"
import { modalConfig } from "../../../../theme"
import { statusTextMap, tagMap } from "../../../../Utils/utils"
import Creators from "../../reducer"
import { buildNotifyPayload, isNotifiableStatusChange } from "../../utils/notify"
import { RejectRequestModal } from "../Modal/RejectRequestModal"
import { NotifyMerchantCheckbox } from "../NotifyMerchantCheckbox"

const { Text } = Typography

const statusConfirmTitle = {
  request_status: "Credit request status",
  provider_status: "Provider status"
}

const statusConfirmContent = {
  request_status: [
    "Are you sure you want to set this status for all selected requests?",
    "Setting a new status may affect credit balance for corresponding business accounts. Note that for requests with “Approved” and “Auto-approved” statuses no further changes can be applied."
  ],
  provider_status: [
    "Are you sure you want to change Provider status for all selected requests?",
    "Note that changing Provider status may affect Request status values and credit balance for corresponding business accounts."
  ]
}

export const ChangeStatusBlock = ({ selectedRequestId, options }) => {
  const dispatch = useDispatch()
  const { modal } = App.useApp()
  const isMobile = useSelector((state) => state.nav.isMobile)
  const [isOpen, setIsOpen] = useState(false)
  const notifyRef = useRef(true)

  const changeStatusConfirmAll = (data) => {
    const isRequestStatusRejected =
      data?.["status_type"] === "request_status" &&
      data?.["status_value"]?.toLowerCase() === "rejected"

    if (isRequestStatusRejected) {
      setIsOpen(true)
    } else {
      const title = statusConfirmTitle?.[data?.["status_type"]]
      const content = statusConfirmContent?.[data?.["status_type"]]
      const isNotifiable = isNotifiableStatusChange(data)

      notifyRef.current = true

      modal.confirm({
        ...modalConfig("info"),
        centered: !isMobile,

        title: title,
        content: (
          <>
            {content?.map((el) => (
              <Fragment key={el}>
                <Text type="secondary">{el}</Text>
                <br />
              </Fragment>
            ))}
            {isNotifiable && (
              <div style={{ marginTop: 16 }}>
                <NotifyMerchantCheckbox onChange={(checked) => (notifyRef.current = checked)} />
              </div>
            )}
          </>
        ),
        cancelText: "Go back",
        okText: "Change status for all",
        cancelButtonProps: { size: "large" },
        okButtonProps: { size: "large" },
        onOk() {
          dispatch(
            Creators.updateCreditRequest({
              ids: selectedRequestId?.join(","),
              status_type: data?.["status_type"],
              status_value: data?.["status_value"],
              ...(isNotifiable ? buildNotifyPayload(notifyRef.current) : {})
            })
          )
        }
      })
    }
  }

  return (
    <div className={cls("change-status-block", { empty: !selectedRequestId?.length })}>
      {!selectedRequestId?.length ? null : (
        <>
          <Divider style={{ margin: "0" }} />
          <Flex
            justify="space-between"
            align={"center"}
            style={{ padding: "10px 0" }}
            className={"active"}
          >
            <Text type="secondary">
              <Text>{selectedRequestId.length}</Text>
              {` item${selectedRequestId.length > 1 ? "s" : ""} selected`}
            </Text>

            <Flex align="center">
              <Dropdown
                menu={{
                  items: options?.["request_status"]?.map((el) => {
                    let statusLowerCase = el?.toLowerCase()
                    return {
                      key: el,
                      label: (
                        <Flex gap={8} align="center">
                          <Badge status={tagMap[statusLowerCase] || "default"} />
                          <Text strong>{statusTextMap[el] || el}</Text>
                        </Flex>
                      )
                    }
                  }),
                  onClick: ({ key, domEvent }) => {
                    domEvent.stopPropagation()
                    changeStatusConfirmAll({
                      ids: selectedRequestId?.join(","),
                      status_type: "request_status",
                      status_value: key
                    })
                  }
                }}
              >
                <Flex gap={8} align="center">
                  <Text strong>Request status</Text>
                  <Icon type="chevron_down_small" style={{ marginLeft: -4 }} />
                </Flex>
              </Dropdown>

              <Divider type="vertical" style={{ height: "16px" }} />

              <Dropdown
                menu={{
                  items: options?.["provider_status"]?.map((el) => {
                    let statusLowerCase = el?.toLowerCase()
                    return {
                      key: el,
                      label: (
                        <Flex gap={8} align="center">
                          <Badge status={tagMap[statusLowerCase] || "default"} />
                          <Text strong>{statusTextMap[el] || el}</Text>
                        </Flex>
                      )
                    }
                  }),
                  onClick: ({ key, domEvent }) => {
                    domEvent.stopPropagation()
                    changeStatusConfirmAll({
                      ids: selectedRequestId?.join(","),
                      status_type: "provider_status",
                      status_value: key
                    })
                  }
                }}
              >
                <Flex gap={8} align="center">
                  <Text strong>Provider status</Text>
                  <Icon type="chevron_down_small" style={{ marginLeft: -4 }} />
                </Flex>
              </Dropdown>
            </Flex>
          </Flex>

          <RejectRequestModal isOpen={isOpen} setIsOpen={setIsOpen} ids={selectedRequestId} />
        </>
      )}
    </div>
  )
}
